import { Component, Watch } from "vue-property-decorator";
import Vue from 'vue';
import FeatureConfig from "@/components/commons/Config/FeatureConfig";
import BaseData from "@/types/Config/BaseData";
import { Getter } from "vuex-class";
import EnumItem from "@/types/fields/EnumItem";

/** 
 * Template à utiliser sur tous les panneaux de fonction de composant Config.
 * Il étend FeatureConfig pour ajouter une gestion de bloc de données (DataBlock)
 * Il synchronise les données pour WidgetConfig.vue
 * @template TData correspond à la structure à définir de DataBlock spécialisée pour une Feature
 */
@Component
export default class FeatureConfigWData<TData extends BaseData> extends FeatureConfig {
    /** Dernier Id de DataBlock */
    protected lastId: number = 0;

    /** Liste des blocs de données */
    protected dataBlocks: TData[] = [] as TData[];

    /** Ajoute un nouveau bloc de données
     * @param item Le datablock à ajouter
     * @param maxLength Nombre maximum de datablocks
     */
    protected newData(item: TData, maxLength: number = undefined) {
        if (maxLength && maxLength == this.dataBlocks.length) {
            this.$bvToast.toast( this.$i18n.t('LimitDatablockReached', {maxLength : maxLength}).toString() , { // `La limite de ${maxLength} blocs de données a été atteinte.`
                title: this.$i18n.t('AddDatablock').toString(), //"Ajout bloc de données",
                autoHideDelay: 3000,
                variant: "warning",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });

            return;
        }

        (this.dataBlocks as TData[]).push(item);

        // Ajout de la donnée dans les CommonWork
        if (this.configs && ('WorkParameters' in this.configs) && this.configs.WorkParameters &&
            ('CommonWorkParameters' in this.configs.WorkParameters) && this.configs.WorkParameters.CommonWorkParameters &&
            ('datas' in this.configs.WorkParameters.CommonWorkParameters) && this.configs.WorkParameters.CommonWorkParameters.datas &&
            Array.isArray(this.configs.WorkParameters.CommonWorkParameters.datas)) {
            let workItem: { data: number, group: number, decimal: number, units: [] } = {
                data: item.Id, group: undefined, decimal: 0, units: undefined
            };
            (this.configs.WorkParameters.CommonWorkParameters.datas as {
                data: number, group: number, decimal: number, units: []
            }[]).push(workItem);
        }

        this.lastId = item.Id;
    }

    /** Supprime un bloc de données */
    protected removeData(index: number) {
        (this.dataBlocks as []).splice(index, 1);

        // Re-calcul des Id pour qu'ils se suivent
        let i = 0;
        for (let dataBlock of this.dataBlocks)
            dataBlock.Id = ++i;

        // Suppression de la donnée dans les CommonWork
        if (this.configs && ('WorkParameters' in this.configs) && this.configs.WorkParameters &&
            ('CommonWorkParameters' in this.configs.WorkParameters) && this.configs.WorkParameters.CommonWorkParameters &&
            ('datas' in this.configs.WorkParameters.CommonWorkParameters) && this.configs.WorkParameters.CommonWorkParameters.datas &&
            Array.isArray(this.configs.WorkParameters.CommonWorkParameters.datas) &&
            this.configs.WorkParameters.CommonWorkParameters.datas.length > index) {
            (this.configs.WorkParameters.CommonWorkParameters.datas as []).splice(index, 1);

            // Re-calcul des Id pour qu'ils se suivent
            for (let j = 0; j < (this.configs.WorkParameters.CommonWorkParameters.datas as []).length; j++) {
                Vue.set(this.configs.WorkParameters.CommonWorkParameters.datas[j], 'data', j+1);
            }
        }

        this.lastId = i;
        console.debug('FeatureConfigWData.ts - removeData - this.dataBlocks, this.lastId, ' +
            'this.configs.WorkParameters.CommonWorkParameters.datas', this.dataBlocks, this.lastId,
            this.configs.WorkParameters.CommonWorkParameters.datas);
    }

    /**
     * Permet de supprimer dans le CommmonWorkParameters la partie Data concernant l'impact en cas de changement d'unité dans la configuration
     * @param index index du bloc de données
     * @protected
     */
    protected changeConfigCommonWorkData(index: number) {
        console.debug("FeatureConfigWData - changeConfigCommonWorkData", this.dataBlocks);

        // Vérification des données changées et suppression du CommonWoks si changement impactant
        if (('WorkParameters' in this.configs) && this.configs.WorkParameters &&
            ('CommonWorkParameters' in this.configs.WorkParameters) && this.configs.WorkParameters.CommonWorkParameters &&
            ('datas' in this.configs.WorkParameters.CommonWorkParameters) && this.configs.WorkParameters.CommonWorkParameters.datas &&
            Array.isArray(this.configs.WorkParameters.CommonWorkParameters.datas) &&
            this.configs.WorkParameters.CommonWorkParameters.datas.length > index &&
            this.dataBlocks.length > index) {

            // Suppression de datas
            (this.configs.WorkParameters.CommonWorkParameters.datas as []).splice(index, 1);

            // Création de nouvel élément datas pour CommonWorkParameters
            let workItem: { data: number, group: number, decimal: number, units: [], bddUnits: [] } = {
                data: this.dataBlocks[index].Id, group: undefined, decimal: 0, units: undefined, bddUnits: undefined
            };

            // Ajout du nouvel element
            (this.configs.WorkParameters.CommonWorkParameters.datas as {
                data: number, group: number, decimal: number, units: [], bddUnits: []
            }[]).splice(index, 0, workItem);
        }
    }

    /** Obtient un nouvel Id de DataBlock */
    protected getNewId() {
        return this.lastId + 1;
    }

    /** Fonction de vérification d'un datablock à l'ajout */
    private addingChecker: (dataBlock: TData) => any;

    /** Vérifie la validité des DataBlocks */
    protected dataBlocksInvalidity(invalidityChecker: (dataBlock) => any): boolean {
        // Verification type de données existantes dans chaque donnée
        if (this.configs && 'Data' in this.configs) {
            for (let i = 0; i < this.configs["Data"].length; i++) {
                return invalidityChecker(this.configs["Data"][i]);
            }
        }

        return false;
    }

    /** Mise à jour de la configuration */
    @Watch('dataBlocks', { deep: true })
    protected updateConfigData() {
        // Init du dictionnaire de configs
        Vue.delete(this.configs, 'Data');

        console.debug("FeatureConfigWData - updateConfigData - this.configs : ", this.configs);

        /** @todo Ajouter le traitement des données hors dataBlocks */

        let datas: TData[] = [];
        // Copie le contenu de la clé Data du dictionnaire this.configs dans une variable locale pour ajouter le datablock à l'existant
        if ('Data' in this.configs)
            datas = [...(this.configs['Data'] as TData[])];

        for (let dataBlock of this.dataBlocks) {
            /** @todo Gérer pour n'insérer que les données ajoutées ou modifiées */
            if (this.addingChecker(dataBlock))
                datas.push(dataBlock);
        }

        // Ajoute la liste des blocks de données actuelle au dictionnaire contenant toutes les données de la feature Evolution
        Vue.set(this.configs, 'Data', datas);

        console.debug("FeatureConfigWData - updateConfigData - this.configs : ", this.configs);
    }

    /** Initialisation des DataBlock de données pour la feature
     * @param defaultDataBlock DataBlock par défaut créé à l'initialisation
     * @param addingChecker Fonction de vérification des DataBlock avant ajout
     */
    protected init(defaultDataBlock: TData, addingChecker: (dataBlock) => any) {
        if (this.configs && "Data" in this.configs && this.configs["Data"])
            this.loadDataMask(this.configs["Data"]);

        // Création d'un bloc de données par défaut s'il n'en existe pas déjà un
        if (this.dataBlocks && this.dataBlocks.length == 0 && defaultDataBlock != undefined)
            this.newData(defaultDataBlock);
        else if(this.configs["Data"] == undefined)
            this.configs["Data"] = [];

        /** Initialise le vérificateur de DataBlock sur ajout */
        if (addingChecker)
            this.addingChecker = addingChecker;
    }

    /** 
     * Chargement de la partie Data du masque de configuration dans les DataBlocks
     * @param newValue liste des DataBlock à charger
     * @param oldValue ancienne valeurs des DataBlock
     *  */
    @Watch('SelectedMask.Parameters.Data')
    protected loadDataMask(newValue, oldValue = undefined) {
        console.debug("FeatureConfigWData - loadDataMask - newValue : %O - this.dataBlocks : %O", newValue, this.dataBlocks);

        if (newValue && this.dataBlocks) {
            //Récupération de la liste des DataBlocks
            if (Array.isArray(newValue))
                this.dataBlocks = newValue;
            else {
                if (this.dataBlocks == undefined)
                    this.dataBlocks = [];
                this.dataBlocks.push(newValue);
            }

            // Met à jour le lastId de DataBlock
            this.lastId = Math.max(...this.dataBlocks.map(x => x.Id));
        }
    }


    /** Getter pour obtenir une liste d'énumération */
    @Getter('GetListEnums')
    private listEnums!: (listName: string) => EnumItem[];

    /** Création du titre pour les feature disposant de DataBlock */
    protected GetDataBlocksTitle(): string {
        let title = "";

        if (this.configs && "Feature" in this.configs) {
            for (let i = 0; i < this.dataBlocks.length; i++) {
                // Type de synthèse
                let synthesis = this.listEnums('SynthesisType|' + this.configs["Feature"]).find(x => x.Id == (this.dataBlocks[i] as any).Synthesis);

                if (synthesis) {
                    title += this.$t('Synthesis').toString() + " " /* "Synthèse "*/ + synthesis.Label + " - ";

                    if (('Type' in (this.dataBlocks[i] as any))) {
                        // Type de données
                        let dataType = this.listEnums('SynthesisData|' + this.configs["Feature"]).find(x => x.Id == (this.dataBlocks[i] as any).Type);

                        if (dataType) {
                            title += dataType.Label;

                            // Séparateur entre chaque donnée
                            title += " | ";
                        }
                    }
                }
            }
        }
        console.debug("FeatureConfigWData - GetDataBlocksTitle() - title", title);

        return title;
    }

    /** Chargement des listes en commun pour les fonctions utilisant des DataBlocks */
    async created() {
        /** Chargement des énumérations : Type de Flux, Type de Relevé, Type de Données */
        await this.$store.dispatch("LoadListEnums", { featureName: this.configs["Feature"] ?? undefined, enumNames: ['SynthesisData', 'StatementType'] });

        /** Chargement des listes : Facteurs */
        await this.$store.dispatch("LoadListItems", [{ listName: 'Factors' }]);

    }
}
